import { msg } from '@lingui/core/macro';
import {
  DefaultError,
  InfiniteData,
  keepPreviousData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { CreateUpdateEntityBody, Endpoints } from '@/api';
import { NotFoundError } from '@/utils/error/NotFoundError';
import { useFetch } from '@/utils/fetch';

const FIVE_MINUTES = 5 * 60 * 1000;

export const TRAITS = [
  {
    id: 'aftersales' as const,
    label: msg({
      id: 'entities.trait.aftersales.label',
      message: 'After-Sales',
    }),
  },
  {
    id: 'store' as const,
    label: msg({
      id: 'entities.trait.store.label',
      message: 'Store',
    }),
  },
  {
    id: 'workshop' as const,
    label: msg({
      id: 'entities.trait.workshop.label',
      message: 'Workshop',
    }),
  },
  {
    id: 'stockLocation' as const,
    label: msg({
      id: 'entities.trait.stock-location.label',
      message: 'Stock location',
    }),
  },
];

export type EntityTrait = (typeof TRAITS)[number]['id'];

export const TYPES = [
  {
    id: 'internal' as const,
    label: msg({
      id: 'entities.trait.internal.label',
      message: 'Internal',
    }),
  },
  {
    id: 'external' as const,
    label: msg({
      id: 'entities.trait.external.label',
      message: 'External',
    }),
  },
];

export type EntityType = (typeof TYPES)[number]['id'];

export type Entity =
  | Endpoints['GET /entities']['response']['entities'][number]
  | Endpoints['GET /entities/:id']['response'];

type EntityAddresses = {
  address: Entity['address'];
  logisticsAddress: Entity['logisticsAddress'];
};

export function getEntityShippingAddress(entity: EntityAddresses | null | undefined) {
  if (!entity) {
    return null;
  }

  return entity.logisticsAddress ?? entity.address ?? null;
}

export const useEntities = (
  params?: Endpoints['GET /entities']['query'],
  options: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /entities']>();

  return useQuery({
    queryKey: ['entities', params],
    queryFn: () => fetch('/entities', params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    enabled: options?.enabled ?? true,
  });
};

export const useEntity = (id: string) => {
  const fetch = useFetch<Endpoints['GET /entities/:id']>();

  return useQuery({
    queryKey: ['entities', id],
    queryFn: () => fetch(`/entities/${id}`),
  });
};

export const useInfiniteEntitiesAndGroups = (
  params?: Endpoints['GET /entities-and-groups']['query']
) => {
  const fetch = useFetch<Endpoints['GET /entities-and-groups']>();
  const limit = params?.limit ?? 20;

  return useInfiniteQuery<
    Endpoints['GET /entities-and-groups']['response'],
    DefaultError,
    InfiniteData<Endpoints['GET /entities-and-groups']['response']>,
    QueryKey,
    number
  >({
    queryFn: ({ pageParam }) => fetch(`/entities-and-groups`, { ...params, offset: pageParam }),
    queryKey: ['entities-and-groups', 'infinite', params],
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.count < limit) {
        return undefined;
      } else {
        return lastPage.meta.offset + limit;
      }
    },
    staleTime: FIVE_MINUTES,
  });
};

export const useEntityLogo = (entityId: string) => {
  const fetch = useFetch<Endpoints['GET /entities/:entityId/logo']>();

  return useQuery({
    queryFn: () =>
      fetch(`/entities/${entityId}/logo`).catch((error) => {
        if (error instanceof NotFoundError) {
          return null;
        } else {
          throw error;
        }
      }),
    queryKey: ['entities', entityId, 'logo'],
  });
};

export const useUpdateEntity = (entityId?: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /entities/:id']>();

  return useMutation({
    mutationFn: (body: CreateUpdateEntityBody) =>
      fetch(`/entities/${entityId}`, undefined, { method: 'PATCH', body }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['entities', entityId] });
    },
  });
};

export const useUpdateEntitySubLocationOptions = (entityId?: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /entities/:id/location']>();

  return useMutation({
    mutationFn: (body: Endpoints['PATCH /entities/:id/location']['body']) =>
      fetch(`/entities/${entityId}/place`, undefined, { method: 'PATCH', body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['entities', entityId] }),
  });
};

export const useUploadLogo = (entityId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /entities/:id/logo']>();

  return useMutation({
    mutationFn: (body: { file: File }) =>
      fetch(
        `/entities/${entityId}/logo`,
        undefined,
        {
          method: 'POST',
          body: body as any,
        },
        'multipart/form-data'
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['entities', entityId] });
    },
  });
};
